import request from '@/api/request'

/**
 * Data Insight API — CloudWatch Logs Insights browser.
 *
 * Backed by cfes-admin-service `InsightsController` under the admin-tool
 * base path (see `@/api/request` → baseURL `/cfes/fodhandler/admintool`):
 *
 *   GET  /cfes/fodhandler/admintool/insights/log-groups
 *   GET  /cfes/fodhandler/admintool/insights/log-groups/match
 *   POST /cfes/fodhandler/admintool/insights/query
 *   POST /cfes/fodhandler/admintool/insights/search
 *   POST /cfes/fodhandler/admintool/insights/logAnalytics
 */

/**
 * List available CloudWatch log groups for the dropdown.
 * Response: Set<String> serialized as a JSON array of log group names.
 */
export function dataInsightListLogGroups(prefix = '') {
  return request({
    url: '/insights/log-groups',
    method: 'get',
    params: prefix ? { prefix } : undefined,
  })
}

/**
 * List log groups whose name matches the given (case-insensitive) regex.
 */
export function dataInsightMatchLogGroups({ regex, prefix = '', limit = 100 } = {}) {
  return request({
    url: '/insights/log-groups/match',
    method: 'get',
    params: { regex, prefix, limit },
  })
}

/**
 * Query CloudWatch logs via Logs Insights fragment.
 *
 * @param {object} body
 * @param {number}   body.startMs
 * @param {number}   body.endMs
 * @param {string[]} body.logGroupNames
 * @param {string}   [body.queryFragment]
 * @param {number}   [body.pageNumber=1]
 * @param {number}   [body.pageSize=25]
 */
export function dataInsightQueryLogs({
  startMs,
  endMs,
  logGroupNames,
  queryFragment = '',
  pageNumber = 1,
  pageSize = 25,
}) {
  return request({
    url: '/insights/query',
    method: 'post',
    data: {
      startMs,
      endMs,
      logGroupNames,
      queryFragment,
      pageNumber,
      pageSize,
    },
  })
}

/**
 * Search CloudWatch logs by plain keyword.
 *
 * @param {object} body
 * @param {number}   body.startMs
 * @param {number}   body.endMs
 * @param {string[]} body.logGroupNames
 * @param {string}   body.keyword
 * @param {number}   [body.pageNumber=1]
 * @param {number}   [body.pageSize=25]
 */
export function dataInsightSearchLogs({
  startMs,
  endMs,
  logGroupNames,
  keyword,
  pageNumber = 1,
  pageSize = 25,
}) {
  return request({
    url: '/insights/search',
    method: 'post',
    data: {
      startMs,
      endMs,
      logGroupNames,
      keyword,
      pageNumber,
      pageSize,
    },
  })
}

/**
 * Run an arbitrary CloudWatch Logs Insights query and preserve its raw,
 * potentially dynamic result columns (for example stats/parse projections).
 */
export function dataInsightLogAnalytics({
  startMs,
  endMs,
  logGroupNames,
  queryFragment = '',
  pageNumber = 1,
  pageSize = 25,
}) {
  return request({
    url: '/insights/logAnalytics',
    method: 'post',
    data: {
      startMs,
      endMs,
      logGroupNames,
      queryFragment,
      pageNumber,
      pageSize,
    },
  })
}

